import { atom, computed } from 'nanostores';
import { toast } from 'react-toastify';
import type Stripe from 'stripe';
import { listProducts } from '~/lib/.client/api/stripe';
import { hmrPropertyFactory } from '~/utils/hmr';
import { logger } from '~/utils/logger';
import { extractModeFromStripeSecretKey } from '~/utils/stripe-helpers';
import { withSpinner } from '~/utils/withSpinner';

interface RefreshProductsOptions {
  onlyIfEmpty?: boolean;
}

const hmr = hmrPropertyFactory('StripeStore');

export class StripeStore {
  @hmr()
  isConfigured = atom(false);

  @hmr()
  isModalOpened = atom(false);

  @hmr()
  hasEdgeFunctions = atom(false);

  @hmr()
  isConfigVisible = atom(false);

  modalTab = atom<'api-key' | 'edit-product'>('api-key');

  partialKey = atom('');

  mode = computed(this.partialKey, (partialKey) => {
    return extractModeFromStripeSecretKey(partialKey);
  });

  selectedProducts = atom<string[]>([]);

  products = atom<Stripe.Product[]>([]);
  isRefreshingProducts = atom(false);

  openModal = () => {
    if (this.partialKey.get() !== '') {
      this.modalTab.set('edit-product');
    }

    this.isModalOpened.set(true);
  };

  openModalToEditApiKey = () => {
    this.modalTab.set('api-key');
    this.isModalOpened.set(true);
  };

  openModalToEditProducts = () => {
    this.modalTab.set('edit-product');
    this.isModalOpened.set(true);
  };

  setProducts(products: Stripe.Product[]) {
    this.products.set(products);
  }

  async refreshProducts(projectId: string, { onlyIfEmpty }: RefreshProductsOptions = {}) {
    if (this.isRefreshingProducts.value) {
      return;
    }

    if (this.products.get().length !== 0 && onlyIfEmpty) {
      return;
    }

    try {
      await withSpinner({
        action: async () => {
          const fetchedProducts = await listProducts(projectId);

          this.products.set(fetchedProducts);
        },
        onStateUpdate: (isSpinning) => {
          this.isRefreshingProducts.set(isSpinning);
        },
        minDuration: 200,
      });
    } catch (error) {
      logger.error(error);
      toast.error('Failed to refresh products');
    }
  }
}

export const stripeStore = new StripeStore();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { stripeStore });
}
